// Ügyfél-detail „Fiókok" szekció — az ügyfélhez tartozó fiókok TÖMÖR listája (`?clientId={id}`).
//
// SZÁNDÉKOSAN NEM a lista-oldal kicsinyített mása. Korábban a közös `DataTable` renderelt itt: kártya-felület,
// oszlop-fejlécek, fix 24rem magasság belső görgetéssel, és soronként fiók-név + weboldal-link +
// kategória/típus-badge-ek + cím + kapcsolattartó-chipek + státusz. Egy detail-oldalon ez „oldal az oldalban":
// a szülő ügyfél alapadatait nyomja el a saját gyerek-listája. A szekció feladata annyi, hogy MEGMUTASSA, mi
// tartozik ide, és ODAVIGYEN — a részletek, szűrők és tömeges műveletek helye a Fiókok lista-oldal.
//
// Ezért három információ marad (a közös `RelatedList` három slotja): **fiók-név · cím · státusz**. A
// darabszám-felirat is kikerült: a lista maga megmutatja, mennyi van, a szám külön mondatban redundáns.
//
// A LISTA NEM CSONKUL (user-döntés): minden fiók látszik, a szekció alján pedig a „Részletes megtekintés"
// link visz a SZŰRT fiók-listára (`/accounts?clientId=`). A korábbi „Összesen 27 — az első 8 látszik"
// zsákutca volt: megmondta, hogy hiányzik valami, de nem mutatta meg, és nem is vitt sehova.
import { type AccountRowSchema } from '@atlas/contracts';
import { BuildingOffice2Icon, PlusIcon } from '@heroicons/react/24/outline';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import { AccountStatusBadge } from '@/components/accounts/account-status-badge';
import { FormSection } from '@/components/shared/form-section';
import { RelatedList, type RelatedListItem } from '@/components/shared/related-list';
import { Button } from '@/components/ui/button';
import { accountKeys } from '@/features/accounts/api';
import { CLIENT_SECTION } from '@/features/clients/sections';
import { api } from '@/lib/api';
import { usePermissions } from '@/lib/use-permissions';
import { isForbiddenStatus } from '@/lib/error-status';

type AccountRow = z.infer<typeof AccountRowSchema>;

export function ClientAccountsSection({ clientId }: { clientId: string }): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canManage = can('accounts.manage');

  // Az ügyfél fiókjai (név szerint, ASC). A kulcs az `accountKeys.lists()` prefix alá esik, így egy fiók
  // create/delete (invalidateKeys: accountKeys.lists()) ezt a szekciót is frissíti.
  const listKey = [...accountKeys.lists(), 'by-client', clientId];
  const query = api.accounts.useQuery(listKey, {
    query: { clientId, sort: 'name', order: 'asc', limit: 200 },
  });
  // A ts-rest react-query wrapper `data`-ja CSAK a 2xx választ hordozza; a 401/403 az `error`-ba esik.
  const rows: AccountRow[] = query.data?.status === 200 ? query.data.body.accounts : [];
  const errStatus = query.error?.status;

  const items: RelatedListItem[] = rows.map((a) => ({
    id: a.id,
    title: a.name,
    // Cím EGY sorban: „1234 Város, Utca 5." — az ország-név a lista-oldalon marad (itt zajt tenne).
    meta: [[a.zip, a.city].filter(Boolean).join(' '), a.address].filter(Boolean).join(', '),
    status: a.status ? (
      <AccountStatusBadge
        status={a.status}
        label={t(`accounts.statusLabel.${a.status}`, { defaultValue: a.status })}
      />
    ) : undefined,
  }));

  return (
    <FormSection id={CLIENT_SECTION.accounts} title={t('clients.accountsTitle')}>
      <RelatedList
        items={items}
        isLoading={query.isLoading}
        isError={query.isError}
        forbidden={isForbiddenStatus(errStatus)}
        onRetry={() => void query.refetch()}
        onItemClick={(item) => void navigate({ to: '/accounts/$id', params: { id: item.id } })}
        // MIND A FIÓK LÁTSZIK (user-döntés): eddig 8 után „Összesen 27 — az első 8 látszik" állt, ami egy
        // zsákutca volt (a fiók-listának akkor még nem volt ügyfél-szűrője, tehát nem is volt hova vinni).
        // Élesben a legnagyobb ügyfélnek 27 fiókja van — kirenderelve is rövid szekció.
        maxVisible={Infinity}
        showAllLabel={(total) => t('clients.relatedTotal', { count: total })}
        // A LÁB-LINK a SZŰRT fiók-listára visz (`?clientId=`) — ott vannak a szűrők, az oszlopok és a
        // tömeges műveletek. A deep-link a hozzárendelés-lista mintáját követi.
        footerAction={{
          label: t('common.openFilteredList'),
          onClick: () => void navigate({ to: '/accounts', search: { clientId } }),
        }}
        emptyLabel={t('clients.accountsEmpty')}
        emptyIcon={BuildingOffice2Icon}
        errorLabel={t('clients.relatedError')}
        forbiddenLabel={t('clients.relatedForbidden')}
        retryLabel={t('clients.relatedRetry')}
      />

      {/* A „hozzáadás" belépő a szekció TARTALMÁNAK VÉGÉN, BALRA (user-döntés, 2026-08-18) — ugyanaz a
          gomb-alak, mint a profil „Jelszó módosítása" / „2FA beállítása" gombja. Korábban a fejléc jobb
          szélén ült: ott vizuálisan nehezebb volt a halk `overline` címnél, és négy szekciónál egy egész
          gomb-oszlop uralta a lapot. A tartalom végén viszont ott van, ahol a felhasználó a lista átnézése
          UTÁN jár. Nincs `mt-*` — a térközt a `FormSection` `space-y-4`-e adja.
          A fiók create SAJÁT OLDAL (nem sheet), a szülő ügyféllel előtöltve → zárolt választó. */}
      {canManage && (
        <div className="flex">
          <Button
            type="button"
            variant="outline"
            onClick={() => void navigate({ to: '/accounts/new', search: { clientId } })}
          >
            <PlusIcon />
            {t('clients.addAccount')}
          </Button>
        </div>
      )}
    </FormSection>
  );
}
